import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import ShopHeader from '@/components/commerce/ShopHeader';
import ShopFooter from '@/components/commerce/ShopFooter';
import MobileBottomNav from '@/components/commerce/MobileBottomNav';
import CartDrawer from '@/components/commerce/CartDrawer';
import Hero from '@/components/commerce/Hero';
import ProductCard from '@/components/commerce/ProductCard';
import { getCategoryIcon } from '@/components/commerce/categoryIcon';
import { FaqSection, ImageTextSection, NewsletterSection } from '@/components/commerce/ContentSections';
import { useShopOrg } from '@/context/ShopOrgContext';
import { deriveProductCollections } from '@/lib/commerceProducts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_LAYOUT_BY_TEXT_POSITION = {
  centrum: 'background_image_centered_text',
  links: 'background_image_text_left',
  rechts: 'background_image_text_right',
};
const HERO_HEIGHT_MAP = { klein: 'compact', midden: 'normal', groot: 'large' };

// Adapts the legacy flat HeroBannerSettings shape (still what this page
// fetches, see fetchShop below) into the commerce_theme ThemeSection shape
// that Hero.jsx now renders -- mirrors backend/commerce_theme.py's
// _build_hero_section_from_legacy so the same component renders identically
// regardless of which endpoint the data came from.
const heroSectionFromLegacySettings = (hero) => {
  const hasImage = Boolean(hero.background_image_url);
  return {
    enabled: Boolean(hero.enabled),
    layout_variant: hasImage
      ? (HERO_LAYOUT_BY_TEXT_POSITION[hero.text_position] || 'background_image_centered_text')
      : 'minimal_no_image',
    settings: {
      title: hero.title,
      subtitle: hero.subtitle,
      primary_cta: (hero.button_text || hero.button_link)
        ? { text: hero.button_text, link: hero.button_link }
        : null,
      text_color: hero.text_color,
      overlay: { enabled: hasImage, color: hero.overlay_color, strength: hero.overlay_opacity },
      height: HERO_HEIGHT_MAP[hero.height] || 'normal',
      image: hasImage
        ? { source_type: 'external_url', media_id: null, external_url: hero.background_image_url, alt_text: null, focal_point: null }
        : null,
    },
  };
};

const ProductRow = ({ title, products, shopSlug }) => {
  const navigate = useNavigate();
  if (products.length === 0) return null;
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} onClick={() => navigate(`/${shopSlug}/shop/product/${product.slug}`)} />
        ))}
      </div>
    </section>
  );
};

const CategoriesSection = ({ title, categories, shopSlug }) => {
  const navigate = useNavigate();
  if (categories.length === 0) return null;
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold text-slate-900">{title || 'Categorieën'}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {categories.map((category) => (
          <button
            key={category.id}
            type="button"
            onClick={() => navigate(`/${shopSlug}/shop/products?category=${category.id}`)}
            className="flex flex-col items-center gap-2 bg-white rounded-2xl shadow-sm p-4 hover:shadow-md transition-shadow"
          >
            <span className="text-2xl">{getCategoryIcon(category.name)}</span>
            <span className="text-sm text-slate-700 text-center line-clamp-1">{category.name}</span>
          </button>
        ))}
      </div>
    </section>
  );
};

// Fallback titles when a migrated section's title was cleared by an admin.
const PRODUCT_ROW_TITLE_FALLBACK = {
  featured_products: 'Uitgelicht',
  new_products: 'Nieuw binnen',
  bestseller_products: 'Meest verkocht',
  deals_products: 'Acties',
};

const ShopHomePage = () => {
  const { id: orgId, slug: shopSlug, name: organizationName, logoUrl } = useShopOrg();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get('preview_token');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [bestsellerIds, setBestsellerIds] = useState([]);
  const [heroSettings, setHeroSettings] = useState({});
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');

  const fetchShop = useCallback(async () => {
    try {
      setLoading(true);
      // A preview link (see ThemeEditorPage's "Voorbeeldlink delen") shows the
      // unpublished draft instead of the live theme -- falls back to the
      // normal published fetch if the token is missing/expired/invalid.
      const themeRequest = previewToken
        ? axios.get(`${API}/public/commerce/${orgId}/theme/preview`, { params: { token: previewToken } })
            .catch(() => axios.get(`${API}/public/commerce/${orgId}/theme`))
        : axios.get(`${API}/public/commerce/${orgId}/theme`);

      const [categoriesRes, productsRes, bestsellersRes, heroRes, themeRes] = await Promise.all([
        axios.get(`${API}/public/commerce/${orgId}/categories`),
        axios.get(`${API}/public/commerce/${orgId}/products`),
        axios.get(`${API}/public/commerce/${orgId}/bestsellers`),
        axios.get(`${API}/public/commerce/${orgId}/hero-banner`),
        // Sections/theme published via the new vormgeving-editor. Null until an
        // admin has opened that editor and published at least once -- until
        // then this page keeps rendering from the legacy hero-banner + fixed
        // section order below, exactly as before.
        themeRequest,
      ]);
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
      setBestsellerIds(bestsellersRes.data.map((entry) => entry.product_id));
      setHeroSettings(heroRes.data || {});
      setTheme(themeRes.data || null);
    } catch (error) {
      toast.error('Kon winkel niet laden');
    } finally {
      setLoading(false);
    }
  }, [orgId, previewToken]);

  useEffect(() => {
    fetchShop();
  }, [fetchShop]);

  useEffect(() => {
    if (searchValue.trim()) {
      navigate(`/${shopSlug}/shop/products?q=${encodeURIComponent(searchValue)}`);
    }
  }, [searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const heroSection = heroSectionFromLegacySettings(heroSettings);
  const productsByRowType = deriveProductCollections(products, bestsellerIds);
  const { featured_products: featured, new_products: newIn, bestseller_products: bestsellers, deals_products: deals } = productsByRowType;
  const homeSections = theme?.pages?.home?.sections;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-teal-200"></div>
          <p className="text-slate-500">Winkel laden...</p>
        </div>
      </div>
    );
  }

  const globalSettings = theme?.settings;

  return (
    <div
      className="min-h-screen bg-slate-50 pb-20 sm:pb-0"
      style={globalSettings?.colors?.page_background ? { backgroundColor: globalSettings.colors.page_background } : undefined}
    >
      <Toaster position="top-right" richColors />
      {previewToken && (
        <div className="sticky top-0 z-50 bg-amber-500 text-white text-center text-xs sm:text-sm font-medium py-2 px-4">
          Voorbeeldmodus — dit toont het concept, nog niet gepubliceerd
        </div>
      )}
      <ShopHeader organizationName={organizationName} logoUrl={logoUrl} searchValue={searchValue} onSearchChange={setSearchValue} />
      <CartDrawer />

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-8">
        {homeSections ? (
          [...homeSections]
            .sort((a, b) => a.position - b.position)
            .filter((section) => section.enabled)
            .map((section) => {
              if (section.type === 'hero') {
                return (
                  <Hero
                    key={section.id}
                    organizationName={organizationName}
                    section={section}
                    theme={globalSettings}
                    onCtaClick={() => navigate(`/${shopSlug}/shop/products`)}
                  />
                );
              }
              if (section.type === 'categories') {
                return (
                  <CategoriesSection
                    key={section.id}
                    title={section.settings?.title}
                    categories={categories}
                    shopSlug={shopSlug}
                  />
                );
              }
              if (section.type === 'image_text') {
                return <ImageTextSection key={section.id} section={section} theme={globalSettings} />;
              }
              if (section.type === 'faq') {
                return <FaqSection key={section.id} section={section} />;
              }
              if (section.type === 'newsletter') {
                return <NewsletterSection key={section.id} section={section} orgId={orgId} theme={globalSettings} />;
              }
              const rowProducts = productsByRowType[section.type];
              if (rowProducts) {
                return (
                  <ProductRow
                    key={section.id}
                    title={section.settings?.title || PRODUCT_ROW_TITLE_FALLBACK[section.type]}
                    products={rowProducts}
                    shopSlug={shopSlug}
                  />
                );
              }
              return null;
            })
        ) : (
          <>
            {heroSection.enabled && (
              <Hero
                organizationName={organizationName}
                section={heroSection}
                onCtaClick={() => navigate(`/${shopSlug}/shop/products`)}
              />
            )}
            <CategoriesSection categories={categories} shopSlug={shopSlug} />
            <ProductRow title="Uitgelicht" products={featured} shopSlug={shopSlug} />
            <ProductRow title="Nieuw binnen" products={newIn} shopSlug={shopSlug} />
            <ProductRow title="Meest verkocht" products={bestsellers} shopSlug={shopSlug} />
            <ProductRow title="Acties" products={deals} shopSlug={shopSlug} />
          </>
        )}

        {products.length === 0 && (
          <p className="text-center text-slate-500 py-12">Deze winkel heeft nog geen producten.</p>
        )}
      </main>

      <ShopFooter organizationName={organizationName} footer={globalSettings?.footer} />
      <MobileBottomNav />
    </div>
  );
};

export default ShopHomePage;
